import { Part } from '@/javascript/music-objects.ts';
import { Box, MantineTheme, Text } from '@mantine/core';
import { createStyles } from '@mantine/emotion';
import React from 'react';
import { LibraryConstants } from './Library.tsx';
import { LibraryPartSection } from './LibraryPartSection.tsx';

export interface LibraryPartRenderProps {
	partIndex: number;
	part: Part;
	height: number;
	measureWidth: number;
	selectedSectionIds: string[];
	selectSectionId: (id: string) => void;
	deselectSectionId: (id: string) => void;
	clearSelection: () => void;
	dragging: boolean;
}

const useStyles = createStyles((_: MantineTheme) => ({
	libraryPart: {
		position: 'relative',
	},
	partLabel: {
		display: 'flex',
		alignItems: 'center',
		textAlign: 'center',
		borderBottom: '2px solid #585858',
		borderRight: '2px solid #585858',
		position: 'sticky',
		left: 0,
		zIndex: 5,
		justifyContent: 'center',
	},
	sectionsContainer: {
		position: 'absolute',
		top: 0,
		// We will set height/width dynamically from props
	},
}));

export const LibraryPart: React.FC<LibraryPartRenderProps> = (props) => {
	const {
		partIndex,
		part,
		height,
		measureWidth,
		selectedSectionIds,
		selectSectionId,
		deselectSectionId,
		dragging,
	} = props;
	const { classes } = useStyles();

	const name = part.instrument?.name ?? part.name;
	const fullWidth = part.getLength() * measureWidth;

	return (
		<Box
			className={classes.libraryPart}
			style={{
				height,
				width: fullWidth + LibraryConstants.partLabelWidth,
				pointerEvents: dragging ? 'none' : 'auto',
			}}
		>
			<Text
				size="xs"
				className={classes.partLabel}
				style={{
					width: LibraryConstants.partLabelWidth,
					height,
					fontSize: 11,
					padding: "2px"
				}}
				bg="white"
			>
				{name}
			</Text>

			<Box
				className={classes.sectionsContainer}
				style={{
					height,
					width: fullWidth,
					left: LibraryConstants.partLabelWidth,
				}}
			>
				{part.sections.map((section, index, sections) => {
					const leftOffset = sections
						.slice(0, index)
						.reduce((acc, s) => acc + s.getLength() * measureWidth, 0);

					return (
						<LibraryPartSection
							key={section.id}
							index={partIndex}
							section={section}
							selected={selectedSectionIds.includes(section.id)}
							dragging={dragging}
							measureWidth={measureWidth}
							selectSectionId={selectSectionId}
							deselectSectionId={deselectSectionId}
							leftOffset={leftOffset}
						/>
					);
				})}
			</Box>
		</Box>
	);
};
